import { useQuery } from "@apollo/client";
import { Alert, Button, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { DocumentDialogContainer } from "components/Document/shared";
import { documentTitle } from "encamp-shared/src/waste/shippingDocumentLabels";
import { gql } from "generated-graphql";
import { useMemo, useState } from "react";

import { completedCorrections, CorrectionDraft } from "./correctionDraft";
import { CORRECTABLE_WASTE_LINES_QUERY, ManifestOption } from "./CorrectionManifestPicker";
import {
  CorrectionSelection,
  correctionSelectionHighlight,
  selectedCorrectionField,
} from "./correctionReviewSelection";
import { ExceedanceKey } from "./exceedanceKeys";
import { ManifestCorrectionReviewFields } from "./ManifestCorrectionReviewFields";

export const CORRECTION_REVIEW_DOCUMENT_QUERY = gql(`
  query GeneratorStatusCorrectionReviewDocument($search: String!) {
    processedManifests(search: $search, page: 0, pageSize: 1) {
      items {
        id
        epaId
        documentManifestId
        manifestPdf {
          id
          downloadLink
          fileExtension
        }
      }
    }
  }
`);

const CORRECTION_REVIEW_FIELDS_QUERY = gql(`
  query GeneratorStatusCorrectionReviewFields($wasteManifestId: ID!) {
    documentManifestDetail(wasteManifestId: $wasteManifestId) {
      wasteManifest { id generatorId }
      wasteLines {
        wasteLineNumber quantity qtyUnitOfMeasureCode nonHazWasteDescription
        federalWasteCodes stateWasteCodes
      }
      reading {
        manifestPageNumber
        wasteLines { wasteLineNumber pageNumber }
      }
    }
  }
`);

function useCorrectionReviewData(
  exceedanceKey: ExceedanceKey,
  processedManifestId: string,
  tenantId: string
) {
  const linesQuery = useQuery(CORRECTABLE_WASTE_LINES_QUERY, {
    variables: {
      facilityId: exceedanceKey.facilityId,
      source: exceedanceKey.source,
      periodStart: exceedanceKey.periodStart,
      processedManifestId: processedManifestId,
    },
  });
  const documentQuery = useQuery(CORRECTION_REVIEW_DOCUMENT_QUERY, {
    variables: { search: `tenantId:${tenantId} id:${processedManifestId}` },
  });
  const record = documentQuery.data?.processedManifests.items[0];
  const document = record?.manifestPdf;
  const fieldsQuery = useQuery(CORRECTION_REVIEW_FIELDS_QUERY, {
    variables: { wasteManifestId: record?.documentManifestId ?? "" },
    skip: !record?.documentManifestId,
  });
  const detail = fieldsQuery.data?.documentManifestDetail;
  const lines = useMemo(
    () =>
      [...(linesQuery.data?.generatorStatusExceedanceCorrectableWasteLines ?? [])].sort((a, b) =>
        a.wasteLineNumber.localeCompare(b.wasteLineNumber, undefined, { numeric: true })
      ),
    [linesQuery.data]
  );
  return {
    record,
    document,
    detail,
    lines,
    loading: linesQuery.loading || documentQuery.loading || fieldsQuery.loading,
    error: linesQuery.error || documentQuery.error || fieldsQuery.error,
    previewLoading: documentQuery.loading,
    refetch: () => {
      void linesQuery.refetch();
      void documentQuery.refetch();
      if (record?.documentManifestId) void fieldsQuery.refetch();
    },
  };
}

export function CorrectionFieldReview({
  exceedanceKey,
  manifest,
  tenantId,
  draft: initialDraft,
  onConfirm,
  onClose,
}: {
  exceedanceKey: ExceedanceKey;
  manifest: ManifestOption;
  tenantId: string;
  draft: CorrectionDraft;
  onConfirm: (draft: CorrectionDraft) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(initialDraft);
  const [selection, setSelection] = useState<CorrectionSelection>({ field: "", lineId: "" });
  const [pageCount, setPageCount] = useState<number | null>(null);
  const { record, document, detail, lines, loading, error, previewLoading, refetch } =
    useCorrectionReviewData(exceedanceKey, manifest.id, tenantId);
  const selected = useMemo(
    () =>
      selectedCorrectionField({
        selection,
        lines,
        documentManifest: detail,
        generatorId: detail?.wasteManifest.generatorId ?? record?.epaId,
      }),
    [selection, lines, detail, record?.epaId]
  );
  const highlight = useMemo(
    () =>
      correctionSelectionHighlight({
        selected,
        reading: detail?.reading,
        pageCount,
      }),
    [selected, detail?.reading, pageCount]
  );
  const ready = !loading && !error && !!record;
  const canConfirm = ready && !selection.field && completedCorrections(draft) != null;
  const confirmHint = selection.field
    ? "Add, update, or cancel the current correction first"
    : "Add at least one correction";

  return (
    <DocumentDialogContainer
      open
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      title="Correct generator status data"
      subheader={`${documentTitle(manifest)} · Review the document while recording corrections.`}
      previewUrl={document?.downloadLink ?? undefined}
      fileExtension={document?.fileExtension ?? "pdf"}
      previewLoading={previewLoading}
      previewDefaultScale="page-width"
      previewHighlight={highlight}
      onPreviewPageCount={setPageCount}
      slotProps={{ paper: { sx: { height: "90vh" } } }}
      formColumnSize={{ xs: 12, md: 5 }}
      previewColumnSize={{ xs: 12, md: 7 }}
      formContainerSx={{ display: "block", pr: { xs: 0, md: 3 } }}
      previewContainerSx={{ minHeight: { xs: 360, md: 0 } }}
      primaryActionButton={
        <Tooltip title={canConfirm ? "" : confirmHint}>
          <span>
            <Button variant="contained" disabled={!canConfirm} onClick={() => onConfirm(draft)}>
              Use corrections
            </Button>
          </span>
        </Tooltip>
      }
    >
      <Stack spacing={2}>
        {loading && <CircularProgress size={24} aria-label="Loading manifest" />}
        {error && (
          <Alert severity="error" action={<Button onClick={refetch}>Retry</Button>}>
            Could not load the manifest for review.
          </Alert>
        )}
        {ready && (
          <>
            {!document && (
              <Typography variant="body2" color="text.secondary">
                No PDF is attached to this manifest. You can still record a correction below.
              </Typography>
            )}
            <ManifestCorrectionReviewFields
              lines={lines}
              selection={selection}
              onSelectionChange={setSelection}
              selected={selected}
              draft={draft}
              onChange={setDraft}
              processedManifestId={manifest.id}
            />
          </>
        )}
        {!loading && !error && !record && <Alert severity="error">Manifest not found.</Alert>}
      </Stack>
    </DocumentDialogContainer>
  );
}
